/**
 * 5e SRD feat database — names and ability score prerequisites.
 *
 * Sources: 5e SRD 5.1 (Creative Commons). Only mechanical prerequisites
 * are stored here (minimum ability scores). Other prerequisites (race,
 * spellcasting, proficiency) are noted as comments but not enforced —
 * the DM can override via the UI toggle if needed.
 *
 * The feat list also includes common PHB feats by name + prereqs for
 * practical DM use, but no descriptions (those come from the books).
 */

import type { AbilityScore } from './character';
import type { Prerequisite } from './character';

export interface FeatDef {
  name: string;
  /** Minimum ability scores required. Empty = no stat prereqs. */
  prereqs: Prerequisite;
  /** Human-readable prereq note (e.g. "Spellcasting ability"). */
  note?: string;
}

/**
 * Standard 5e feats with ability score prerequisites.
 * Sorted alphabetically. Only stat-based prereqs are enforced;
 * other requirements (race, class feature, etc.) are in `note`.
 */
export const FEAT_DATA: FeatDef[] = [
  { name: 'Alert', prereqs: {} },
  { name: 'Athlete', prereqs: {} },
  { name: 'Actor', prereqs: {} },
  { name: 'Charger', prereqs: {} },
  { name: 'Crossbow Expert', prereqs: {} },
  { name: 'Defensive Duelist', prereqs: { DEX: 13 } },
  { name: 'Dual Wielder', prereqs: {} },
  { name: 'Dungeon Delver', prereqs: {} },
  { name: 'Durable', prereqs: {} },
  { name: 'Elemental Adept', prereqs: {}, note: 'Spellcasting ability required' },
  { name: 'Grappler', prereqs: { STR: 13 } },
  { name: 'Great Weapon Master', prereqs: {} },
  { name: 'Healer', prereqs: {} },
  { name: 'Heavily Armored', prereqs: {}, note: 'Medium armor proficiency required' },
  { name: 'Heavy Armor Master', prereqs: {}, note: 'Heavy armor proficiency required' },
  { name: 'Inspiring Leader', prereqs: { CHA: 13 } },
  { name: 'Keen Mind', prereqs: {} },
  { name: 'Lightly Armored', prereqs: {} },
  { name: 'Linguist', prereqs: {} },
  { name: 'Lucky', prereqs: {} },
  { name: 'Mage Slayer', prereqs: {} },
  { name: 'Magic Initiate', prereqs: {} },
  { name: 'Martial Adept', prereqs: {} },
  { name: 'Medium Armor Master', prereqs: {}, note: 'Medium armor proficiency required' },
  { name: 'Mobile', prereqs: {} },
  { name: 'Moderately Armored', prereqs: {}, note: 'Light armor proficiency required' },
  { name: 'Mounted Combatant', prereqs: {} },
  { name: 'Observant', prereqs: {} },
  { name: 'Polearm Master', prereqs: {} },
  { name: 'Resilient', prereqs: {} },
  { name: 'Ritual Caster', prereqs: { INT: 13 }, note: 'INT or WIS 13+ required' },
  { name: 'Savage Attacker', prereqs: {} },
  { name: 'Sentinel', prereqs: {} },
  { name: 'Sharpshooter', prereqs: {} },
  { name: 'Shield Master', prereqs: {} },
  { name: 'Skilled', prereqs: {} },
  { name: 'Skulker', prereqs: { DEX: 13 } },
  { name: 'Spell Sniper', prereqs: {}, note: 'Spellcasting ability required' },
  { name: 'Tavern Brawler', prereqs: {} },
  { name: 'Tough', prereqs: {} },
  { name: 'War Caster', prereqs: {}, note: 'Spellcasting ability required' },
  { name: 'Weapon Master', prereqs: {} },
];

/** Case-insensitive feat lookup. Returns undefined for homebrew feats. */
export function lookupFeat(name: string): FeatDef | undefined {
  const lower = name.toLowerCase().trim();
  return FEAT_DATA.find(f => f.name.toLowerCase() === lower);
}

/** Get all feat names for autocomplete. */
export function featNames(): string[] {
  return FEAT_DATA.map(f => f.name);
}

/**
 * Check if a feat's ability score prerequisites are met.
 * Returns { met: true } or { met: false, failures: [...] }.
 */
export function checkFeatPrereqs(
  feat: FeatDef,
  scores: Partial<Record<AbilityScore, number>>,
): { met: boolean; failures: string[] } {
  const failures: string[] = [];
  for (const [stat, required] of Object.entries(feat.prereqs) as [AbilityScore, number][]) {
    const current = scores[stat] ?? 10;
    if (current < required) {
      failures.push(`${stat} ${current} < ${required}`);
    }
  }
  return { met: failures.length === 0, failures };
}
